// ask-scope.ts — pure scope-chip logic for the /ask pinned/inferred filter row (S4-3, spec
// 2026-07-31-query-spa-surface §4.1/§4.3).
//
// Scope is a NARROWING relevance filter the client re-sends on every ask()/regenerate(). Two chip
// origins that must never be conflated:
//   pinned   — the USER'S restriction, thread state held by useAskQuery, survives turns, only the
//              user clears it.
//   inferred — what the PLANNER added on its own for ONE answer, read off that turn's `scope` SSE
//              frame. It never enters the request and never survives the turn (the planner re-infers
//              per question); "removing" it is a display dismissal plus the §8 telemetry event.
//
// Pure and stateless so the reducer tests (spec §6.4) run without a DOM: useAskQuery holds the
// pinned state; AskScopeBar renders chips derived here.
import type { QueryScope } from '@terum/contracts';
import { siteLabel } from './sites';
import { formatSourceDate } from './formatting';

export type ScopeFacet = keyof QueryScope; // 'sites' | 'since' | 'until' | 'people' | 'projectIds' | 'entryKinds'

export interface ScopeChip {
  facet: ScopeFacet;
  /** The raw facet value ('' for scalar facets like `since`, where the facet IS the value). */
  value: string;
  /** Display text, already formatted ("Slack", "since Jul 1", a person's name). */
  label: string;
  origin: 'pinned' | 'inferred';
  /** True when the engine reported this pinned person/project matched nothing (frame.unresolved) —
   *  rendered as a warning, NEVER silently widened (spec §4.1). */
  unresolved?: boolean;
  /** True when the engine's empty-retry dropped this planner facet mid-run (frame.relaxed) — the
   *  chip renders struck-through so the reader knows the answer is NOT restricted by it. */
  relaxed?: boolean;
}

// The scope frame as the stream delivers it (contract member of QueryStreamEventSchema, minus `type`).
export interface ScopeFrame {
  pinned?: QueryScope | null;
  inferred?: QueryScope | null;
  relaxed?: string[] | null;
  unresolved?: string[] | null;
}

/** Normalize to meaningful content; undefined when nothing remains. Mirrors the server's
 *  canonicalScope so "no scope" is `undefined` (and the request body omits the key) — never `{}`. */
export function normalizeScope(scope: QueryScope | null | undefined): QueryScope | undefined {
  if (!scope) return undefined;
  const out: QueryScope = {};
  const sites = [...new Set(scope.sites ?? [])].sort();
  if (sites.length > 0) out.sites = sites;
  if (scope.since) out.since = scope.since;
  if (scope.until) out.until = scope.until;
  const people = (scope.people ?? []).filter((p) => p.trim() !== '');
  if (people.length > 0) out.people = people;
  if ((scope.projectIds ?? []).length > 0) out.projectIds = scope.projectIds ?? [];
  if ((scope.entryKinds ?? []).length > 0) out.entryKinds = scope.entryKinds ?? [];
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Pin one site. Capability "single" (the shipped engine) REPLACES any existing pin rather than
 *  accumulating — the route 400s on two sites, and a rejected request is a worse surprise than a
 *  swapped chip. S4-9 flips this to accumulate when the capability goes "multi". */
export function pinSite(scope: QueryScope | undefined, siteId: string, capability: 'single' | 'multi'): QueryScope {
  const prev = scope ?? {};
  if (capability === 'multi') {
    const sites = [...new Set([...(prev.sites ?? []), siteId])].sort();
    return { ...prev, sites };
  }
  return { ...prev, sites: [siteId] };
}

/** Pin the `since` lower bound (inclusive UTC day, YYYY-MM-DD). */
export function pinSince(scope: QueryScope | undefined, isoDay: string): QueryScope {
  return { ...(scope ?? {}), since: isoDay };
}

/** Remove one pinned value. Scalar facets clear; array facets drop the one value. Returns undefined
 *  when the scope is now empty, so the next request omits `scope` entirely (cache-key parity). */
export function unpin(scope: QueryScope | undefined, facet: ScopeFacet, value: string): QueryScope | undefined {
  if (!scope) return undefined;
  const next: QueryScope = { ...scope };
  if (facet === 'since' || facet === 'until') {
    delete next[facet];
  } else {
    const arr = (next[facet] ?? []).filter((v) => v !== value);
    if (arr.length > 0) next[facet] = arr;
    else delete next[facet];
  }
  return normalizeScope(next);
}

function chipsOf(scope: QueryScope | null | undefined, origin: 'pinned' | 'inferred'): ScopeChip[] {
  const s = normalizeScope(scope);
  if (!s) return [];
  const chips: ScopeChip[] = [];
  for (const id of s.sites ?? []) chips.push({ facet: 'sites', value: id, label: siteLabel(id), origin });
  if (s.since) chips.push({ facet: 'since', value: s.since, label: `since ${formatSourceDate(s.since) ?? s.since}`, origin });
  if (s.until) chips.push({ facet: 'until', value: s.until, label: `until ${formatSourceDate(s.until) ?? s.until}`, origin });
  for (const p of s.people ?? []) chips.push({ facet: 'people', value: p, label: p, origin });
  // Project NAMES live in the facet inventory; the chip renders the id's short form until the bar
  // resolves it (AskScopeBar owns that lookup — this module stays inventory-free and pure).
  for (const id of s.projectIds ?? []) chips.push({ facet: 'projectIds', value: id, label: id.slice(0, 8), origin });
  for (const k of s.entryKinds ?? []) chips.push({ facet: 'entryKinds', value: k, label: k, origin });
  return chips;
}

/**
 * Derive the chip row: the thread's pinned scope (always, every turn — "nothing is silent", §4.3)
 * plus the LAST turn's inferred facets. A turn with no frame (cache hit, older server) contributes
 * nothing — inferred chips never outlive the turn that produced them.
 *
 * `dismissed` holds `facet:value` keys of inferred chips the user clicked off THIS turn (display
 * dismissal + telemetry; the planner re-infers per question, so there is nothing to un-send).
 */
export function deriveChips(
  pinned: QueryScope | undefined,
  lastFrame: ScopeFrame | null | undefined,
  dismissed: ReadonlySet<string>,
): ScopeChip[] {
  const pinnedChips = chipsOf(pinned, 'pinned');
  const unresolved = new Set(lastFrame?.unresolved ?? []);
  for (const c of pinnedChips) {
    if ((c.facet === 'people' || c.facet === 'projectIds') && unresolved.has(c.value)) c.unresolved = true;
  }
  const relaxed = new Set(lastFrame?.relaxed ?? []);
  const inferredChips = chipsOf(lastFrame?.inferred, 'inferred')
    .filter((c) => !dismissed.has(chipKey(c)))
    .map((c) => (relaxed.has(c.facet) ? { ...c, relaxed: true } : c));
  return [...pinnedChips, ...inferredChips];
}

export function chipKey(c: Pick<ScopeChip, 'facet' | 'value'>): string {
  return `${c.facet}:${c.value}`;
}

/** Deterministic typed-ahead over a facet inventory list — prefix/token match on the label and id,
 *  case-insensitive. Legitimate as plain string matching (no-shortcut rule): the user is picking
 *  from a KNOWN list, not free text (spec §4.2 determinism note). */
export function matchFacetRows<T extends { id: string; label: string }>(rows: T[], query: string): T[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((r) => {
    const hay = `${r.label} ${r.id}`.toLowerCase();
    return hay.split(/[\s/-]+/).some((tok) => tok.startsWith(q)) || hay.startsWith(q);
  });
}

/** UTC day N days back, YYYY-MM-DD — the `since` presets. UTC-pinned like every date in the ask
 *  surface (bug-502 lesson: a locale-shifted day boundary dates the same note two ways). */
export function utcDayAgo(days: number, now: number): string {
  const d = new Date(now - days * 86400000);
  return d.toISOString().slice(0, 10);
}

/** Whether a date-input value is committable as a `since` pin. A native date input fires change on
 *  EVERY intermediate value: in M/D/Y-order locales the first year keystroke yields a complete-but-
 *  garbage date (year 0002) — committing it would pin a nonsense filter and unmount the picker
 *  mid-typing, making typed dates unreachable (S4-3 review-confirmed, 3/3 in-browser). No corpus
 *  content predates 1900, so a sub-1900 year can only be an intermediate keystroke state; the
 *  calendar widget always produces the full year in one event and commits immediately. */
export function isCommittableSinceDay(isoDay: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(isoDay) && isoDay >= '1900-01-01';
}
